import LoadingSpinner from '../../../../../shared/components/loading-spinner/LoadingSpinner';
import CohortDefinition from '../../../../common/cohort/components/characteristic/CohortDefinition';
import { ICohortVessel } from '../../../../common/cohort/cohortTypes';
import { TSignalCountsOnSpeedRanges } from '../../../kpiTypes';
import LoadedConditionSpeedDistributionChart from './LoadedConditionSpeedDistributionChart';
import ExpandableContainer from '../../../../../shared/components/expandable-container/ExpandableContainer';
import { useEffect, useRef, useState } from 'react';

interface ISpeedProps {
	vessel: ICohortVessel;
	minSize: number;
	maxSize: number;
	cohortVessels: ICohortVessel[];
	vesselSignalCountsOnSpeedRanges: TSignalCountsOnSpeedRanges | null;
	vesselCohortSignalCountsOnSpeedRanges: TSignalCountsOnSpeedRanges | null;
	minAge: number;
	maxAge: number;
}

const Speed: React.FC<ISpeedProps> = ({ ...props }) => {
	const ref = useRef<HTMLDivElement>(null);

	const [panelOffset, setPanelOffset] = useState<{ top: number; left: number }>();

	const cohortVesselInfo = props.cohortVessels.length === 1 ? props.cohortVessels[0] : props.vessel;

	useEffect(() => {
		const updatePanelSize = () => {
			if (ref.current) {
				const { offsetTop, offsetLeft } = ref.current;
				setPanelOffset({ top: offsetTop, left: offsetLeft });
			}
		};

		// Initial measurement
		updatePanelSize();

		// Update on window resize
		window.addEventListener('resize', updatePanelSize);
		return () => {
			window.removeEventListener('resize', updatePanelSize);
		};
	}, []);

	return (
		<div ref={ref} className="flex flex-row gap-4 w-full h-full">
			<div className="flex flex-col gap-2 w-full">
				<div className="flex-none">
					<CohortDefinition
						title={props.vessel.name}
						shipsNumber={props.cohortVessels.length}
						size={props.vessel.size}
						sizeUnit={props.vessel.sizeUnit}
						age={props.vessel.age}
						isCohort={false}
					/>
				</div>
				<div className="flex-auto border border-msi-darkgrey-color-300 rounded">
					{!props.vesselSignalCountsOnSpeedRanges || props.vesselSignalCountsOnSpeedRanges?.loading ? (
						<LoadingSpinner size="md" spinnerClassName="p-2" />
					) : (
						<ExpandableContainer panelOffsets={panelOffset}>
							{(props.vesselSignalCountsOnSpeedRanges.dataList && (
								<LoadedConditionSpeedDistributionChart
									chartKey={props.vessel.imoNumber}
									signalCountsOnSpeedRangesData={props.vesselSignalCountsOnSpeedRanges.dataList!}
								/>
							)) || <div className="flex items-center justify-center">No data found!</div>}
						</ExpandableContainer>
					)}
				</div>
			</div>
			<div className="flex flex-col gap-2 w-full">
				<div className="flex-none">
					<CohortDefinition
						title="Cohort (Average By Vessel)"
						minSize={props.minSize}
						maxSize={props.maxSize}
						shipsNumber={props.cohortVessels.length}
						size={cohortVesselInfo.size}
						sizeUnit={cohortVesselInfo.sizeUnit}
						age={cohortVesselInfo.age}
						minAge={props.minAge}
						maxAge={props.maxAge}
						isCohort={true}
					/>
				</div>
				<div className="flex-auto border border-msi-darkgrey-color-300 rounded">
					{!props.vesselCohortSignalCountsOnSpeedRanges || props.vesselCohortSignalCountsOnSpeedRanges?.loading ? (
						<LoadingSpinner size="md" spinnerClassName="p-2" />
					) : (
						<ExpandableContainer panelOffsets={panelOffset}>
							{(props.vesselCohortSignalCountsOnSpeedRanges.dataList && (
								<LoadedConditionSpeedDistributionChart
									chartKey={props.vessel.imoNumber}
									signalCountsOnSpeedRangesData={props.vesselCohortSignalCountsOnSpeedRanges.dataList!}
								/>
							)) || <div className="flex items-center justify-center">No data found!</div>}
						</ExpandableContainer>
					)}
				</div>
			</div>
		</div>
	);
};

export default Speed;
